import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useServerHistory } from '../api';
import { toServerDateTime } from '../helpers';
import type { ServerStatsRow } from '../types';

interface Props {
  servers: ServerStatsRow[];
  defaultServerName?: string;
}

function toDateInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fromDateInputValue(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.valueOf()) ? null : d;
}

export function ServerHistoryTab({ servers, defaultServerName }: Props) {
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  }, []);
  const now = useMemo(() => new Date(), []);

  const [serverName, setServerName] = useState(
    defaultServerName ?? servers[0]?.serverName ?? '',
  );
  const [fromInput, setFromInput] = useState(toDateInputValue(yesterday));
  const [toInput, setToInput] = useState(toDateInputValue(now));
  const [committed, setCommitted] = useState({
    name: defaultServerName ?? servers[0]?.serverName ?? '',
    from: '',
    to: '',
  });

  const history = useServerHistory(
    committed.name,
    committed.from,
    committed.to,
    Boolean(committed.from && committed.to && committed.name),
  );

  const search = () => {
    const fromD = fromDateInputValue(fromInput);
    const toD = fromDateInputValue(toInput);
    if (!serverName || !fromD || !toD) return;
    setCommitted({
      name: serverName,
      from: toServerDateTime(fromD),
      to: toServerDateTime(toD),
    });
  };

  const chartData = useMemo(
    () =>
      [...(history.data ?? [])].reverse().map((r) => ({
        time: r.reqDate ? r.reqDate.slice(11, 16) : '',
        cpu: r.cpuUsed,
        memUsed: r.memUsed,
        activeCalls: r.activeCalls,
        load1m: r.load1m,
      })),
    [history.data],
  );

  const columns = useMemo<ColumnDef<ServerStatsRow>[]>(
    () => [
      { accessorKey: 'reqDate', header: 'Date', size: 170 },
      { accessorKey: 'status', header: 'Status', size: 90 },
      {
        accessorKey: 'cpuUsed',
        header: 'CPU %',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono">{row.original.cpuUsed}</span>
        ),
      },
      { accessorKey: 'memUsed', header: 'Mem Used', size: 90 },
      { accessorKey: 'memFree', header: 'Mem Free', size: 90 },
      { accessorKey: 'diskUsed', header: 'Disk Used', size: 90 },
      { accessorKey: 'diskFree', header: 'Disk Free', size: 90 },
      { accessorKey: 'load1m', header: 'Load 1m', size: 80 },
      { accessorKey: 'load5m', header: 'Load 5m', size: 80 },
      { accessorKey: 'load15m', header: 'Load 15m', size: 80 },
      {
        accessorKey: 'activeCalls',
        header: 'Calls',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-emerald-700">
            {(row.original.activeCalls ?? 0).toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
        <div className="space-y-1.5">
          <Label className="text-xs">Server</Label>
          <Select value={serverName} onValueChange={setServerName}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Pick a server" />
            </SelectTrigger>
            <SelectContent>
              {servers.map((s) => (
                <SelectItem key={s.serverName} value={s.serverName}>
                  {s.serverName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input
            type="datetime-local"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input
            type="datetime-local"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs opacity-0">.</Label>
          <Button
            size="sm"
            onClick={search}
            disabled={history.isFetching || !serverName}
          >
            <Search className="h-3.5 w-3.5" />
            Search
          </Button>
        </div>
      </div>

      {history.isFetching ? (
        <Skeleton className="h-72 w-full" />
      ) : (history.data ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card/50 py-12 text-center text-sm text-muted-foreground">
          {committed.from
            ? 'No history rows in this range.'
            : 'Pick a server and a date range, then Search.'}
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-card p-3 shadow-sm">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="time"
                  fontSize={10}
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis
                  yAxisId="left"
                  fontSize={10}
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  fontSize={10}
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="cpu"
                  stroke="#3b82f6"
                  name="CPU %"
                  dot={false}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="memUsed"
                  stroke="#10b981"
                  name="Mem Used (GB)"
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="activeCalls"
                  stroke="#f59e0b"
                  name="Active Calls"
                  dot={false}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="load1m"
                  stroke="#f43f5e"
                  name="Load 1m"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <DataTable<ServerStatsRow, unknown>
            tableId="monitoring:history:v1"
            columns={columns}
            data={history.data ?? []}
            getRowId={(row, i) => String(row.id ?? `${row.reqDate}-${i}`)}
            pageSize={25}
            exportFilename={`history-${serverName}.csv`}
            searchPlaceholder="Search rows…"
            emptyTitle="No rows"
          />
        </>
      )}
    </div>
  );
}
